//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { BodySecondaryText, SubtitleText } from '@components/atoms/text/Text';
import approveRightsSvg from '@images/uia/uia-approve-rights.svg';
import Cuid from '@models/Cuid';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import { ExpiryInfo } from '@uia/rights/rights-expiry/ExpiryInfo';
import { UIARightsSetExpiryScreen } from '@uia/rights/rights-expiry/UIARightsSetExpiryScreen';
import { getIsRightsExpiryV2Enabled } from '@uia/rights/selectors/getUIARights';
import { isDefined } from '@utils/filterPredicates';

interface UIARightsOwnedProps {
  cuid: Cuid;
  expirationTime?: Date;
  isItemSyndicated: boolean;
  onClose: () => void;
  onUpdateRights: () => void;
}

export const UIARightsOwned = ({
  cuid,
  expirationTime,
  isItemSyndicated,
  onClose,
  onUpdateRights,
}: UIARightsOwnedProps) => {
  // TODO: PD-295163 - Remove isRightsExpiryV2Enabled flag once rights expiry V2 is released
  const isRightsExpiryV2Enabled = useSelector(getIsRightsExpiryV2Enabled);
  const [shouldShowUpdateExpiryForm, setShouldShowUpdateExpiryForm] =
    useState(false);

  return !shouldShowUpdateExpiryForm ? (
    <Wrapper>
      <RightsApprovedContainer>
        <RightsApprovedImage alt='rights owned' src={approveRightsSvg} />
        <SubtitleText>Rights Owned</SubtitleText>
        <StyledText>
          You're free to add some products, and distribute this
          {!isDefined(expirationTime) ? ' everywhere' : ''}!<br></br>
        </StyledText>
        {isRightsExpiryV2Enabled && (
          <ExpiryInfo
            expirationTime={expirationTime}
            isDisabled={isItemSyndicated}
            onClick={() => setShouldShowUpdateExpiryForm(true)}
          />
        )}
      </RightsApprovedContainer>

      <UIAFooter>
        <UIAFooter.Actions>
          <UIAFooter.ActionButton
            onClick={() => {
              onClose();
            }}
            trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.OWNED_RIGHTS_DONE_BUTTON}
          >
            Done
          </UIAFooter.ActionButton>
        </UIAFooter.Actions>
      </UIAFooter>
    </Wrapper>
  ) : (
    <UIARightsSetExpiryScreen
      cuid={cuid}
      expiryTime={expirationTime}
      handleOnPreviousButtonClick={() => setShouldShowUpdateExpiryForm(false)}
      onUpdateRightsSuccess={() => {
        setShouldShowUpdateExpiryForm(false);
        onUpdateRights();
      }}
    />
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const RightsApprovedContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: calc(${theme.spacings[1]} * 25); // 100px
  `,
);

const StyledText = styled(BodySecondaryText)(
  ({ theme }) => `
    text-align: center;
    padding: 0 ${theme.spacings[7]};
  `,
);

const RightsApprovedImage = styled.img`
  width: 100px;
`;
